import { useState } from 'react';
import { Alert, Button, Card, PinInput, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { Actor } from '@prooffleet/contracts';
import { ApiError, api } from '@prooffleet/client';
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  currentLocale,
  setLocale,
  type SupportedLocale,
} from '../i18n/index.js';
import { deviceId, useSession } from '@prooffleet/client';

function isSupportedLocale(value: string | null): value is SupportedLocale {
  return value !== null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function Login(): React.JSX.Element {
  const { t } = useTranslation();
  const setSession = useSession((s) => s.setSession);

  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ accessToken: string; actor: Actor }>('/auth/driver-login', {
        method: 'POST',
        body: { phone, pin, deviceId: deviceId() },
      });
      setSession(result.accessToken, result.actor);
    } catch (err) {
      // Отказ по привязке устройства объясняем отдельно: водитель должен
      // понять, что нужно к менеджеру, а не подбирать PIN.
      setError(
        err instanceof ApiError && err.status === 403 ? t('login.deviceBlocked') : t('login.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="xl" maw={480} mx="auto" w="100%">
          <Title order={1}>{t('login.title')}</Title>

          <Card>
            <Stack gap="lg">
              <TextInput
                label={t('login.phone')}
                inputMode="tel"
                autoComplete="tel"
                placeholder="+1 555 000 0001"
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
              />

              <div>
                <Text fw={600} mb="xs">
                  {t('login.pin')}
                </Text>
                <PinInput
                  length={4}
                  type="number"
                  inputMode="numeric"
                  mask
                  value={pin}
                  onChange={setPin}
                />
              </div>

              {error !== null && (
                <Alert color="red" variant="light">
                  {error}
                </Alert>
              )}
            </Stack>
          </Card>

          <Select
            label={t('login.language')}
            data={SUPPORTED_LOCALES.map((code) => ({ value: code, label: LOCALE_LABELS[code] }))}
            value={currentLocale()}
            allowDeselect={false}
            size="xl"
            onChange={(value) => {
              if (isSupportedLocale(value)) setLocale(value);
            }}
          />
        </Stack>
      </div>

      <div className="pf-action-bar">
        <Button
          fullWidth
          loading={busy}
          disabled={phone.length < 6 || pin.length < 4}
          onClick={() => void submit()}
        >
          {t('login.submit')}
        </Button>
      </div>
    </div>
  );
}
